/**
 * Feature settings storage key
 */
export const FEATURE_SETTINGS_KEY = 'decisively.feature.settings';
export const FEATURE_SETTINGS_CHANGED_EVENT = 'decisively.feature.settings.changed';

/**
 * Feature settings structure
 */
export interface FeatureSettings {
  experimental: {
    ai_chat: boolean;
    extended_debug_diagnostics: boolean;
    web_ingestion: boolean;
    simplified_relationship_dialog: boolean;
    release_centre_tabs: boolean;
    tasks: boolean;
    event_resolution: boolean;
    interview_sidebars: boolean;
  };
  legacy_pages: {
    enabled: boolean;
    conversations: boolean;
    reports: boolean;
    bots: boolean;
    reporting: boolean;
    graph_testing: boolean;
    interactive_reports: boolean;
  };
  developer: {
    debug: boolean;
    command_palette: boolean;
  };
  design: {
    compact: boolean;
  };
}

/**
 * Type-safe feature setting paths
 */
export type FeatureSettingPath =
  | 'experimental.ai_chat'
  | 'developer.debug'
  | 'developer.command_palette'
  | 'design.compact'
  | 'experimental.extended_debug_diagnostics'
  | 'experimental.web_ingestion'
  | 'experimental.simplified_relationship_dialog'
  | 'experimental.release_centre_tabs'
  | 'experimental.tasks'
  | 'experimental.event_resolution'
  | 'experimental.interview_sidebars'
  | 'legacy_pages.enabled'
  | 'legacy_pages.conversations'
  | 'legacy_pages.reports'
  | 'legacy_pages.bots'
  | 'legacy_pages.reporting'
  | 'legacy_pages.graph_testing'
  | 'legacy_pages.interactive_reports';

/**
 * Default feature settings
 */
export const DEFAULT_FEATURE_SETTINGS: FeatureSettings = {
  experimental: {
    ai_chat: false,
    extended_debug_diagnostics: false,
    simplified_relationship_dialog: false,
    release_centre_tabs: false,
    web_ingestion: false,
    tasks: false,
    event_resolution: false,
    interview_sidebars: false,
  },
  legacy_pages: {
    enabled: false,
    conversations: false,
    reports: false,
    bots: false,
    reporting: false,
    graph_testing: false,
    interactive_reports: false,
  },
  developer: {
    debug: false,
    command_palette: false,
  },
  design: {
    compact: false,
  },
};

/**
 * Get feature settings from localStorage
 */
export const getFeatureSettings = (): FeatureSettings => {
  try {
    const stored = localStorage.getItem(FEATURE_SETTINGS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        experimental: {
          ...DEFAULT_FEATURE_SETTINGS.experimental,
          ...parsed.experimental,
          // Temporarily unavailable, including previously saved opt-ins.
          event_resolution: false,
        },
        legacy_pages: {
          ...DEFAULT_FEATURE_SETTINGS.legacy_pages,
          ...parsed.legacy_pages,
        },
        developer: {
          ...DEFAULT_FEATURE_SETTINGS.developer,
          ...parsed.developer,
        },
        design: {
          ...DEFAULT_FEATURE_SETTINGS.design,
          ...parsed.design,
        },
      };
    }
  } catch (error) {
    console.error('Failed to parse feature settings:', error);
  }
  return DEFAULT_FEATURE_SETTINGS;
};

/**
 * Save feature settings to localStorage
 */
export const saveFeatureSettings = (settings: FeatureSettings): void => {
  try {
    localStorage.setItem(FEATURE_SETTINGS_KEY, JSON.stringify(settings));
    window.dispatchEvent(new Event(FEATURE_SETTINGS_CHANGED_EVENT));
  } catch (error) {
    console.error('Failed to save feature settings:', error);
  }
};

/**
 * Check if a feature is enabled
 */
export const isFeatureEnabled = (path: FeatureSettingPath): boolean => {
  const settings = getFeatureSettings();
  const [category, ...keys] = path.split('.') as [keyof FeatureSettings, ...string[]];
  let value: unknown = settings[category];

  for (const key of keys) {
    if (value === null || typeof value !== 'object') return false;
    value = (value as Record<string, unknown>)[key];
  }

  if (typeof value === 'boolean') return value;

  return false;
};

export const isFeatureOrParentEnabled = (path: FeatureSettingPath): boolean => {
  const [category, key] = path.split('.');
  return isFeatureEnabled(path) || isFeatureEnabled(`${category}.enabled` as FeatureSettingPath);
};
